import React, { useEffect, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  X,
  Settings2,
  RefreshCw,
  Globe,
  Workflow,
  Database,
  BarChart3,
  PieChart,
  FileText,
  TrendingUp,
  Users,
  MessageSquare,
  FileBarChart,
  LineChart,
  Table,
  ClipboardList,
  Activity,
} from 'lucide-react';
import FilterableMultiSelect from '../../../common/FilterableMultiSelect';
import { authservice } from '../../../../../components/api/authservice';
import { apiService } from '../../../../../components/api/apiService';
import {
  analyticsHubService,
  mapAnalyticsHubChatAgentOption,
} from '../../../../../components/api/analyticsHubService';
import chatAgentService from '../ChatIntents/chatAgentService';
import {
  fetchAvailableWorkflows,
  workflowApiErrorMessage,
  type AvailableWorkflowDto,
} from '../../../workflowbuilder/postWorkflowService';
import type { Theme, ThemeColors, ReportSettings, SourceType } from './types';
import { generateReportId, UNIVERSAL_REPORT_ID } from './utils';

const THEME_CONFIG: Record<Theme, ThemeColors> = {
  dark: {
    primary: 'bg-neutral-900',
    secondary: 'bg-neutral-800',
    background: 'bg-neutral-950',
    surface: 'bg-neutral-800',
    border: 'border-neutral-700',
    text: { primary: 'text-white', secondary: 'text-neutral-300', muted: 'text-neutral-400' },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-neutral-700',
    input: 'bg-neutral-800 border-neutral-600 text-white placeholder-neutral-500',
  },
  light: {
    primary: 'bg-white',
    secondary: 'bg-gray-50',
    background: 'bg-gray-100',
    surface: 'bg-white',
    border: 'border-gray-200',
    text: { primary: 'text-gray-900', secondary: 'text-gray-700', muted: 'text-gray-500' },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-gray-100',
    input: 'bg-white border-gray-300 text-gray-900 placeholder-gray-400',
  },
};

export const REPORT_ICON_OPTIONS: { value: string; label: string; Icon: LucideIcon }[] = [
  { value: 'BarChart3', label: 'Bar Chart', Icon: BarChart3 },
  { value: 'PieChart', label: 'Pie Chart', Icon: PieChart },
  { value: 'LineChart', label: 'Line Chart', Icon: LineChart },
  { value: 'FileText', label: 'Document', Icon: FileText },
  { value: 'FileBarChart', label: 'Report', Icon: FileBarChart },
  { value: 'TrendingUp', label: 'Trends', Icon: TrendingUp },
  { value: 'Users', label: 'Users', Icon: Users },
  { value: 'MessageSquare', label: 'Messages', Icon: MessageSquare },
  { value: 'Table', label: 'Table', Icon: Table },
  { value: 'ClipboardList', label: 'Checklist', Icon: ClipboardList },
  { value: 'Activity', label: 'Activity', Icon: Activity },
  { value: 'Database', label: 'Data', Icon: Database },
];

interface DataModelOption {
  id: string;
  name: string;
  description?: string;
}

interface ReportSettingsModalProps {
  theme?: Theme;
  settings: ReportSettings;
  onClose: () => void;
  onSave: (settings: ReportSettings) => void | Promise<void>;
  primaryActionLabel?: string;
  saving?: boolean;
  subtitle?: string;
}

const defaultSettings = (): ReportSettings => ({
  reportId: generateReportId(),
  isUniversalId: false,
  reportType: 'standalone',
  description: '',
  icon: 'BarChart3',
  isActive: true,
  sourceType: 'workflow',
  workflowId: '',
  dataModelId: '',
  roleAccessList: [],
  userAccessList: [],
  deniedUsers: [],
  allowedDepartmentIds: [],
  copilotIds: [],
  reportHeader: { title: '', subtitle: '' },
});

export { defaultSettings as createDefaultReportSettings };

const ReportSettingsModal: React.FC<ReportSettingsModalProps> = ({
  theme = 'dark',
  settings,
  onClose,
  onSave,
  primaryActionLabel = 'Apply Settings',
  saving = false,
  subtitle = 'Configure metadata, access & data source',
}) => {
  const colors = THEME_CONFIG[theme];
  const [form, setForm] = useState<ReportSettings>(settings);
  const [headerJson, setHeaderJson] = useState(() => JSON.stringify(settings.reportHeader ?? {}, null, 2));
  const [headerError, setHeaderError] = useState<string | null>(null);

  const [roleOptions, setRoleOptions] = useState<{ label: string; value: string }[]>([]);
  const [userOptions, setUserOptions] = useState<{ label: string; value: string }[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<{ label: string; value: string }[]>([]);
  const [copilotOptions, setCopilotOptions] = useState<{ label: string; value: string }[]>([]);
  const [loadingCopilots, setLoadingCopilots] = useState(false);

  const [workflows, setWorkflows] = useState<AvailableWorkflowDto[]>([]);
  const [dataModels, setDataModels] = useState<DataModelOption[]>([]);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);
  const [loadingDataModels, setLoadingDataModels] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  useEffect(() => {
    const loadAccessOptions = async () => {
      try {
        const [roles, users, deptRes] = await Promise.all([
          authservice.getRoles(),
          authservice.getUsers(),
          chatAgentService.getDepartments().catch(() => null),
        ]);
        setRoleOptions(roles.map((r: { roleName: string; roleId: string }) => ({ label: r.roleName, value: r.roleId })));
        setUserOptions(users.map((u: { email: string; id: string }) => ({ label: u.email, value: u.id })));

        const deptList = deptRes?.departments ?? deptRes?.data ?? (Array.isArray(deptRes) ? deptRes : []);
        if (Array.isArray(deptList)) {
          setDepartmentOptions(
            deptList.map((d: { id: string; name?: string; departmentName?: string }) => ({
              label: d.name ?? d.departmentName ?? d.id,
              value: d.id,
            }))
          );
        }
      } catch (err) {
        console.error('Failed to load access options', err);
      }
    };
    loadAccessOptions();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadCopilots = async () => {
      setLoadingCopilots(true);
      try {
        const response = await analyticsHubService.getChatAgents();
        const agents = response?.data ?? [];
        if (!cancelled) {
          setCopilotOptions(
            agents
              .map(mapAnalyticsHubChatAgentOption)
              .filter((opt): opt is { label: string; value: string } => opt !== null)
          );
        }
      } catch (err) {
        console.error('Failed to load copilots', err);
        if (!cancelled) setCopilotOptions([]);
      } finally {
        if (!cancelled) setLoadingCopilots(false);
      }
    };
    void loadCopilots();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (form.sourceType !== 'workflow') return;
    let cancelled = false;
    const load = async () => {
      setLoadingWorkflows(true);
      setLoadError(null);
      try {
        const data = await fetchAvailableWorkflows(true);
        if (!cancelled) setWorkflows(data);
      } catch (e) {
        if (!cancelled) {
          setLoadError(workflowApiErrorMessage(e));
          setWorkflows([]);
        }
      } finally {
        if (!cancelled) setLoadingWorkflows(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [form.sourceType]);

  useEffect(() => {
    if (form.sourceType !== 'datamodel') return;
    let cancelled = false;
    const load = async () => {
      setLoadingDataModels(true);
      setLoadError(null);
      try {
        const data = await apiService.get('/DataModels', {}, 'backend');
        const list = data?.dataModels ?? [];
        if (!cancelled) {
          setDataModels(
            list.map((item: { id: string; modelName: string; description?: string }) => ({
              id: item.id,
              name: item.modelName,
              description: item.description,
            }))
          );
        }
      } catch (err) {
        console.error('Failed to load data models:', err);
        if (!cancelled) {
          setLoadError('Failed to load data models');
          setDataModels([]);
        }
      } finally {
        if (!cancelled) setLoadingDataModels(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [form.sourceType]);

  const patch = (partial: Partial<ReportSettings>) =>
    setForm((prev) => ({ ...prev, ...partial }));

  const handleGenerateId = () => {
    patch({ reportId: generateReportId(), isUniversalId: false });
  };

  const handleSetUniversal = () => {
    patch({ reportId: UNIVERSAL_REPORT_ID, isUniversalId: true });
  };

  const handleSubmit = async () => {
    let reportHeader: Record<string, unknown>;
    try {
      reportHeader = headerJson.trim() ? JSON.parse(headerJson) : {};
      if (typeof reportHeader !== 'object' || reportHeader === null || Array.isArray(reportHeader)) {
        setHeaderError('Report header must be a JSON object');
        return;
      }
    } catch {
      setHeaderError('Invalid JSON in report header');
      return;
    }

    setHeaderError(null);
    await onSave({ ...form, reportHeader });
  };

  const inputCls = `w-full px-2.5 py-1.5 rounded-md border text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-500/50 ${colors.input}`;
  const labelCls = `block text-[10px] font-medium uppercase tracking-wide ${colors.text.muted} mb-1`;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className={`w-full max-w-xl max-h-[90vh] flex flex-col rounded-lg border ${colors.border} ${colors.surface} shadow-2xl overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex-shrink-0 px-4 py-2.5 border-b ${colors.border} flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-indigo-600/20 flex items-center justify-center">
              <Settings2 className="w-3.5 h-3.5 text-indigo-400" />
            </div>
            <div>
              <h3 className={`text-xs font-semibold ${colors.text.primary}`}>Report Settings</h3>
              <p className={`text-[10px] ${colors.text.muted}`}>{subtitle}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`p-1 rounded ${colors.hover} ${colors.text.muted}`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {/* Report ID */}
          <div>
            <label className={labelCls}>Report ID</label>
            <div className="flex gap-1.5">
              <input
                type="text"
                value={form.reportId}
                onChange={(e) => patch({ reportId: e.target.value, isUniversalId: e.target.value === UNIVERSAL_REPORT_ID })}
                className={`flex-1 ${inputCls} font-mono`}
                placeholder="UUID"
              />
              <button
                type="button"
                onClick={handleGenerateId}
                title="Generate new UUID"
                className={`px-2 py-1.5 rounded-md border ${colors.border} ${colors.hover} ${colors.text.secondary} text-[10px] flex items-center gap-1 whitespace-nowrap`}
              >
                <RefreshCw className="w-3 h-3" />
                Generate
              </button>
              <button
                type="button"
                onClick={handleSetUniversal}
                title="Set universal report ID"
                className={`px-2 py-1.5 rounded-md border text-[10px] flex items-center gap-1 whitespace-nowrap transition-colors ${
                  form.isUniversalId
                    ? 'border-indigo-500/50 bg-indigo-600/20 text-indigo-400'
                    : `${colors.border} ${colors.hover} ${colors.text.secondary}`
                }`}
              >
                <Globe className="w-3 h-3" />
                Universal
              </button>
            </div>
            {form.isUniversalId && (
              <p className="text-[10px] text-indigo-400 mt-1">Universal ID — accessible across all tenants/agents</p>
            )}
          </div>

          {/* Report Type */}
          <div>
            <label className={labelCls}>Report Type</label>
            <select
              value={form.reportType}
              onChange={(e) => patch({ reportType: e.target.value as ReportSettings['reportType'] })}
              className={inputCls}
            >
              <option value="chatbot">ChatBot</option>
              <option value="standalone">StandAlone</option>
            </select>
          </div>

          {/* Description */}
          <div>
            <label className={labelCls}>Description</label>
            <textarea
              value={form.description}
              onChange={(e) => patch({ description: e.target.value })}
              rows={2}
              className={`${inputCls} resize-none`}
              placeholder="Brief description of this report..."
            />
          </div>

          {/* Report Header */}
          <div>
            <label className={labelCls}>Report Header</label>
            <textarea
              value={headerJson}
              onChange={(e) => {
                setHeaderJson(e.target.value);
                setHeaderError(null);
              }}
              rows={4}
              spellCheck={false}
              className={`${inputCls} resize-none font-mono`}
              placeholder={'{\n  "title": "Monthly KPIs",\n  "subtitle": "Last 30 days"\n}'}
            />
            <p className={`text-[10px] ${colors.text.muted} mt-1`}>
              JSON metadata for the report header (title, subtitle, badges, etc.)
            </p>
            {headerError && (
              <p className="text-[10px] text-red-400 mt-1">{headerError}</p>
            )}
          </div>

          {/* Icon picker */}
          <div>
            <label className={labelCls}>Icon</label>
            <div className="grid grid-cols-6 gap-1">
              {REPORT_ICON_OPTIONS.map(({ value, label, Icon }) => (
                <button
                  key={value}
                  type="button"
                  title={label}
                  onClick={() => patch({ icon: value })}
                  className={`flex flex-col items-center gap-0.5 p-1.5 rounded-md border transition-colors ${
                    form.icon === value
                      ? 'border-indigo-500/60 bg-indigo-600/15 text-indigo-400'
                      : `${colors.border} ${colors.hover} ${colors.text.muted}`
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                  <span className="text-[8px] truncate w-full text-center">{label.split(' ')[0]}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Is Active */}
          <div className={`flex items-center justify-between py-1.5 px-2.5 rounded-md border ${colors.border}`}>
            <div>
              <p className={`text-[11px] font-medium ${colors.text.primary}`}>Active</p>
              <p className={`text-[10px] ${colors.text.muted}`}>Enable report generation & visibility</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={form.isActive}
              onClick={() => patch({ isActive: !form.isActive })}
              className={`relative w-9 h-5 rounded-full transition-colors ${
                form.isActive ? 'bg-indigo-600' : 'bg-neutral-600'
              }`}
            >
              <span
                className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                  form.isActive ? 'translate-x-4' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>

          {/* Source Type */}
          <div>
            <label className={labelCls}>Source Type</label>
            <select
              value={form.sourceType}
              onChange={(e) => patch({ sourceType: e.target.value as SourceType, workflowId: '', dataModelId: '' })}
              className={inputCls}
            >
              <option value="workflow">Workflow</option>
              <option value="datamodel">DataModel (Direct Execution)</option>
            </select>
          </div>

          {/* Conditional source selector */}
          {form.sourceType === 'workflow' && (
            <div>
              <label className={`${labelCls} flex items-center gap-1`}>
                <Workflow className="w-3 h-3" />
                Workflow
              </label>
              {loadingWorkflows ? (
                <div className={`${inputCls} flex items-center gap-2 ${colors.text.muted}`}>
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  Loading workflows...
                </div>
              ) : (
                <select
                  value={form.workflowId}
                  onChange={(e) => patch({ workflowId: e.target.value })}
                  className={inputCls}
                >
                  <option value="">Select workflow...</option>
                  {workflows.map((wf) => (
                    <option key={wf.id} value={wf.id}>
                      {wf.name}{wf.status ? ` · ${wf.status}` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {form.sourceType === 'datamodel' && (
            <div>
              <label className={`${labelCls} flex items-center gap-1`}>
                <Database className="w-3 h-3" />
                Data Model
              </label>
              {loadingDataModels ? (
                <div className={`${inputCls} flex items-center gap-2 ${colors.text.muted}`}>
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  Loading data models...
                </div>
              ) : (
                <select
                  value={form.dataModelId}
                  onChange={(e) => patch({ dataModelId: e.target.value })}
                  className={inputCls}
                >
                  <option value="">Select data model...</option>
                  {dataModels.map((dm) => (
                    <option key={dm.id} value={dm.id}>
                      {dm.name}
                    </option>
                  ))}
                </select>
              )}
              {form.dataModelId && (
                <p className={`text-[10px] ${colors.text.muted} mt-1`}>
                  {dataModels.find((d) => d.id === form.dataModelId)?.description || 'Direct execution against selected model'}
                </p>
              )}
            </div>
          )}

          {loadError && (
            <p className="text-[10px] text-red-400 px-2 py-1 rounded bg-red-500/10 border border-red-500/20">
              {loadError}
            </p>
          )}

          {/* Access controls */}
          <div className={`pt-2 border-t ${colors.border} space-y-2.5`}>
            <p className={`text-[10px] font-medium uppercase tracking-wide ${colors.text.muted}`}>
              Access Control
            </p>
            <FilterableMultiSelect
              label="Role Access"
              options={roleOptions}
              selected={form.roleAccessList}
              setSelected={(v) => patch({ roleAccessList: v })}
            />
            <FilterableMultiSelect
              label="User Access"
              options={userOptions}
              selected={form.userAccessList}
              setSelected={(v) => patch({ userAccessList: v })}
            />
            <FilterableMultiSelect
              label="Denied Users"
              options={userOptions}
              selected={form.deniedUsers}
              setSelected={(v) => patch({ deniedUsers: v })}
            />
            <FilterableMultiSelect
              label="Allowed Departments (optional)"
              options={departmentOptions}
              selected={form.allowedDepartmentIds}
              setSelected={(v) => patch({ allowedDepartmentIds: v })}
            />
            {loadingCopilots ? (
              <div className={`flex items-center gap-2 text-[10px] ${colors.text.muted} py-1`}>
                <RefreshCw className="w-3 h-3 animate-spin" />
                Loading Copilots…
              </div>
            ) : (
              <FilterableMultiSelect
                label="Copilots"
                options={copilotOptions}
                selected={form.copilotIds}
                setSelected={(v) => patch({ copilotIds: v })}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={`flex-shrink-0 px-4 py-2.5 border-t ${colors.border} flex justify-end gap-2`}>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className={`px-3 py-1.5 rounded-md text-[11px] ${colors.text.secondary} ${colors.hover} disabled:opacity-50`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={saving}
            className="px-3 py-1.5 rounded-md text-[11px] bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-medium"
          >
            {saving ? 'Saving…' : primaryActionLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportSettingsModal;
